import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity } from 'lucide-react';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { RZTable } from '@components/common/table';
import { StatusCell, AssessorCell, DateCell, AssessmentCell } from '@components/common/table';
import { Assessment, TableColumn } from '@app-types';
import { hasOpenConflicts, getTierConflictTooltip } from '@utils/conflictUtils';
import { statusToVariant } from '@utils/badgeMappers';
import { getAssessmentStatusLabel } from '@utils/referenceCatalog';
import { formatInstantAsDate } from '@utils/dateUtils';
import styles from './RecentActivity.module.css';

interface RecentActivityProps {
  assessments: Assessment[];
  isLoading: boolean;
}

// Column configuration
const activityColumns: TableColumn[] = [
  { key: 'assessment', label: 'Assessment', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'scheduled_date', label: 'Scheduled Date', defaultVisible: true },
  { key: 'assessor', label: 'Assessor', defaultVisible: true }
];

const RecentActivity: React.FC<RecentActivityProps> = ({ assessments, isLoading }) => {
  const navigate = useNavigate();

  const handleViewAssessment = (assessmentId: number) => {
    navigate(`/assessments/${assessmentId}`);
  };

  const formatDate = (dateString: string | null | undefined) =>
    formatInstantAsDate(dateString, 'Not scheduled');

  const renderCellContent = (column: TableColumn, assessment: Assessment) => {
    switch (column.key) {
      case 'assessment':
        return (
          <AssessmentCell
            name={assessment.assessment_name}
            supplierName={assessment.supplier_name}
            tierName={assessment.tier_name}
          />
        );

      case 'status':
        return (
          <StatusCell
            hasConflict={hasOpenConflicts(assessment)}
            conflictTooltip={hasOpenConflicts(assessment) ? getTierConflictTooltip(assessment) : undefined}
          >
            <Badge variant={statusToVariant(assessment.assessment_status)}>{getAssessmentStatusLabel(assessment.assessment_status)}</Badge>
          </StatusCell>
        );

      case 'scheduled_date':
        return <DateCell date={assessment.scheduled_date} formatDate={formatDate} />;

      case 'assessor':
        return <AssessorCell assessorName={assessment.assessor_name} assessorEmail={assessment.assessor_email} />;

      default: {
        const value = assessment[column.key as keyof Assessment];
        // Don't render complex objects like conflicts array
        if (typeof value === 'object') return '-';
        return value || '-';
      }
    }
  };

  return (
    <div className={styles.recentActivityContainer}>
      <div className={styles.activityHeader}>
        <h3>Recent Activity</h3>
        <Button variant="ghost" size="sm" onClick={() => navigate('/assessments')}>
          View All
        </Button>
      </div>

      <div className={styles.activityTable}>
        <RZTable
          data={assessments}
          columns={activityColumns}
          getRowKey={(assessment) => assessment.assessment_id}
          renderCell={renderCellContent}
          rowClassName={(assessment) => (hasOpenConflicts(assessment) ? 'has-conflict' : '')}
          onRowClick={(assessment) => handleViewAssessment(assessment.assessment_id)}
          state={{
            isLoading,
            isEmpty: assessments.length === 0,
            loadingMessage: 'Loading recent activity...'
          }}
          emptyState={<RZTable.Empty icon={<Activity size={48} />} title="No recent assessments found" />}
        />
      </div>
    </div>
  );
};

export default RecentActivity;
